/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $wrapNodeInElement, mergeRegister } from '@lexical/utils';
import {
  $createParagraphNode,
  $createRangeSelection,
  $getRoot,
  $getSelection,
  $isNodeSelection,
  $isRangeSelection,
  $setSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_HIGH,
  COMMAND_PRIORITY_LOW,
  createCommand,
  DRAGOVER_COMMAND,
  DRAGSTART_COMMAND,
  DROP_COMMAND,
  FORMAT_ELEMENT_COMMAND,
  LexicalCommand,
  LexicalEditor
} from 'lexical';
import { useEffect, useRef, useState } from 'react';
import * as React from 'react';
import { CAN_USE_DOM } from '../../utils/canUseDOM';
import DropzoneTwygsComponent from '../../../Common/DropzoneTwygsComponent';
import { UPLOAD_FILE_EDITOR_DESC } from '../../../../constants/twygs.constant.js';
import { FILE_TYPES } from '../../../../constants/image.constant.js';
import AvaIcon from '../../../../assets/medium_default_optimized.png';
import ButtonCustom from '../../../../components/Button/index';
import {
  $createImageNode,
  $isImageNode,
  ImageNode,
  ImagePayload
} from '../../nodes/ImageNode/ImageNode';
import { $createCommentableParagraphNode } from '../../nodes/CommentableParagraphNode';
import { MAX_FILE_SIZE_50, ALLOWED_FILE_TYPES_EDITOR } from '../../../../constants/image.constant';
import { fetchListMediaOfContributor } from '../../../../apis/article.api';
import './styles.css';
import { Spinner } from '@chakra-ui/react';
import { useAtom } from 'jotai';
import { contributorAtom } from '../../../../components/Common/Form/textarea-custom-hook-form/atom';
import { $createVideoNode, $isVideoNode, VideoNode, VideoPayload } from '../../nodes/VideoNode';
import { $createAudioNode, $isAudioNode, AudioNode, AudioPayload } from '../../nodes/AudioNode';
import { useNavigate } from 'react-router-dom';
import PlayIcon from '../../../../assets/play-icon.svg';
import VerifiedIcon from '../../../../assets/verified-icon.svg';
import { useQuery } from '@tanstack/react-query';

export type InsertImagePayload = Readonly<ImagePayload>;
export type InsertVideoPayload = Readonly<VideoPayload>;
export type InsertAudioPayload = Readonly<AudioPayload>;

const getDOMSelection = (targetWindow: Window | null): Selection | null =>
  CAN_USE_DOM ? (targetWindow || window).getSelection() : null;

export const INSERT_IMAGE_COMMAND: LexicalCommand<InsertImagePayload> =
  createCommand('INSERT_IMAGE_COMMAND');

export const INSERT_VIDEO_COMMAND: LexicalCommand<InsertVideoPayload> =
  createCommand('INSERT_VIDEO_COMMAND');

export const INSERT_AUDIO_COMMAND: LexicalCommand<InsertAudioPayload> =
  createCommand('INSERT_AUDIO_COMMAND');

export function InsertMediaDialog({
  activeEditor,
  onClose,
  isHiddenContributorSection = false,
  isCommentEditor
}: {
  activeEditor: LexicalEditor;
  onClose: () => void;
  isHiddenContributorSection?: boolean;
  isCommentEditor?: boolean;
}): JSX.Element {
  const hasModifier = useRef(false);
  const [listMedia, setListMedia] = useState([]);
  const [selectedMediaOfContributor, setSelectedMediaOfContributor] = useState<any>({});
  const [error, setError] = useState('');

  const [contributors, setContributors] = useAtom(contributorAtom);
  const navigate = useNavigate();

  const { data: listMediaContributor = [] as any, isFetching } = useQuery({
    queryKey: ['listMediaContributor'],
    queryFn: fetchListMediaOfContributor
  });

  const handleUploadMedia = (files: File[]) => {
    const isVideo = files[0].type.includes('video');
    const isAudio = files[0].type.includes('audio');
    const validationError = validateFiles(files);

    if (validationError) {
      setError(validationError);
      return;
    }
    const payloads: any = files.map((file) => ({
      altText: file.name,
      src: URL.createObjectURL(file),
      file,
      type: isVideo ? 'video' : isAudio ? 'audio' : 'image'
    }));

    activeEditor.dispatchCommand(
      isVideo ? INSERT_VIDEO_COMMAND : isAudio ? INSERT_AUDIO_COMMAND : INSERT_IMAGE_COMMAND,
      payloads
    );

    onClose();
  };

  const validateFiles = (files: File[]) => {
    let messageError = '';
    if (!ALLOWED_FILE_TYPES_EDITOR.includes(files[0].type)) {
      messageError = 'Please ensure your file type is PNG, JPG, JPEG, GIF, MP4, WEBM or OGG';
    } else if (files[0].size > MAX_FILE_SIZE_50) {
      messageError = 'Please ensure your file size is less than 10MB';
    }

    return messageError;
  };

  const handleAddSelectedMediaOfContributor = (selectedMedia, isAddedFromContributorList) => {
    const contributorExists = contributors.some(
      (contributor) => contributor.user.id === selectedMedia.user.id
    );

    if (!contributorExists) {
      setContributors((prevContributors) => [
        ...prevContributors,
        {
          user: {
            id: selectedMedia.user?.id,
            imgUrl: selectedMedia.user?.imgUrl,
            firstName: selectedMedia.user?.firstName,
            username: selectedMedia.user?.username,
            isVerifiedAuthor: selectedMedia.user?.isVerifiedAuthor
          },
          role: {
            name: 'Photographer',
            value: 'photographer'
          },
          equityPercentage: selectedMedia.equityPercentage || 0,
          isCoAuthor: true
        }
      ]);
    }

    const isVideo = selectedMedia.contentTypes[0]?.name === 'Video';
    const isAudio = selectedMedia.contentTypes[0]?.name === 'Audio';

    const payload = {
      altText: selectedMedia.title,
      src: selectedMedia.contentUrl,
      type: isVideo ? 'video' : isAudio ? 'audio' : 'image',
      contributors: selectedMedia.user,
      contributorId: selectedMedia.user.id,
      isAddedFromContributorList
    };

    activeEditor.dispatchCommand(
      isVideo ? INSERT_VIDEO_COMMAND : isAudio ? INSERT_AUDIO_COMMAND : INSERT_IMAGE_COMMAND,
      payload
    );

    activeEditor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');

    onClose();
  };

  useEffect(() => {
    hasModifier.current = false;
    const handler = (e: KeyboardEvent) => {
      hasModifier.current = e.altKey;
    };
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
    };
  }, [activeEditor]);

  useEffect(() => {
    if (!isCommentEditor && listMediaContributor?.data) {
      setListMedia(listMediaContributor.data);
    }
  }, [listMediaContributor, isCommentEditor]);

  return (
    <div className="flex flex-col gap-2 md:gap-6 md:p-0 w-full max-w-[610px] overflow-hidden">
      <p className="text-lg">Upload images or video files</p>
      <div>
        <DropzoneTwygsComponent
          error={error}
          validateFiles={handleUploadMedia}
          w={'full'}
          desc={UPLOAD_FILE_EDITOR_DESC.image.map((item, index) => (
            <p
              key={index}
              className="text-02-dim-gray text-xs w-full max-w-[230px] text-center md:text-left"
            >
              {item}
            </p>
          ))}
          title={
            <p className="text-02-dim-gray text-sm max-w-[230px]">
              Click to upload <span className="text-[#00A375] font-[900]">file</span> or drag and
              drop
            </p>
          }
          acceptFileTypes={FILE_TYPES.twygsImageLexicalEditor}
          isSingle
          notMutiple
        />
        {error && <p className="whitespace-break-spaces !text-red-500 mt-2 text-sm">{error}</p>}
      </div>
      {isHiddenContributorSection || (
        <div className="flex flex-col gap-2 md:gap-6 w-full">
          <p className="text-lg text-center font-[700]">OR</p>

          <div>
            <p className="mb-2 md:mb-6 text-lg text-center md:text-start">
              Choose from submitted content by contributors:
            </p>
            {isFetching ? (
              <div className="flex justify-center w-full items-center h-[200px]">
                <Spinner size="md" color="#4CAF50" />
              </div>
            ) : listMedia.length > 0 ? (
              <div className="flex md:grid md:grid-cols-2 gap-3 max-w-[384px] md:max-w-full md:max-h-[200px] xl:max-h-[350px] md:gap-6 overflow-x-auto md:overflow-y-auto pb-3 md:pb-0 md:pr-3">
                {listMedia?.map((item: any) => (
                  <div
                    key={item.id}
                    className="cursor-pointer border border-04-anti-flash-white rounded-b-lg bg-white hover:opacity-80 min-w-[250px] md:min-w-0 max-w-[254px]"
                    onClick={() => setSelectedMediaOfContributor(item)}
                  >
                    <div className="flex items-center gap-2 p-2 mb-1">
                      <input
                        type="radio"
                        className="w-5 h-5 flex-shrink-0"
                        checked={selectedMediaOfContributor?.id === item.id}
                        onChange={(e) => e.stopPropagation()}
                      />
                      <p className="text-xs font-[600] truncate">{item.title}</p>
                    </div>
                    {item.contentTypes[0]?.name === 'Video' ? (
                      <div className="relative">
                        <video
                          src={item.contentUrl}
                          className="w-[254px] h-[120px] md:h-[176px] object-cover"
                        />
                        <div className="py-2 px-5 absolute rounded-[12px] bg-[#333333CC] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2">
                          <img className="w-9 h-9" src={PlayIcon} alt="" />
                        </div>
                      </div>
                    ) : (
                      <img
                        src={item.contentUrl}
                        className="w-[254px] h-[120px] md:h-[176px] object-cover"
                        alt="media content"
                      />
                    )}
                    <div className="flex items-center gap-2 p-2 mt-1">
                      <p className="text-xs font-[600] whitespace-nowrap">Submitted by:</p>
                      <div
                        className="flex items-center gap-1"
                        onClick={() => navigate(`/${item.user.username}`)}
                      >
                        <img
                          src={item.user.imgUrl || AvaIcon}
                          className="w-6 h-6 flex-shrink-0 rounded-full border border-solid border-04-anti-flash-white object-cover"
                          alt=""
                        />
                        <p
                          title={item.user.firstName}
                          className="text-xs font-[600] hover:text-growth-green transition duration-200 max-w-[70px] truncate"
                        >
                          {item.user.firstName}
                        </p>
                        {item?.user?.isVerifiedAuthor && (
                          <img src={VerifiedIcon} className="w-4 h-4" alt="" />
                        )}
                        {item?.user?.username && (
                          <span className="font-[600] text-[10px] text-02-dim-gray mt-[2px]">
                            @{item?.user?.username}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center w-full h-[200px]">
                <p className="text-lg font-[700]">No media found</p>
              </div>
            )}
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-3 md:gap-6">
            <ButtonCustom className="w-full" variant="outline" onClick={onClose}>
              Discard
            </ButtonCustom>
            <ButtonCustom
              disabled={Object.keys(selectedMediaOfContributor).length === 0}
              className="w-full"
              onClick={() => handleAddSelectedMediaOfContributor(selectedMediaOfContributor, true)}
            >
              Add
            </ButtonCustom>
          </div>
        </div>
      )}
    </div>
  );
}

export default function MediaPlugin({ shouldFocus, isCommentEditor }): JSX.Element | null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (!editor.hasNodes([ImageNode, VideoNode, AudioNode])) {
      throw new Error('MediaPlugin: ImageNode or VideoNode or AudioNode not registered on editor');
    }

    return mergeRegister(
      editor.registerCommand<InsertImagePayload[]>(
        INSERT_IMAGE_COMMAND,
        (payloads) => {
          if (!Array.isArray(payloads)) {
            payloads = [payloads];
          }

          editor.update(() => {
            const selection = $getSelection();

            payloads.forEach((payload) => {
              const imageNode = $createImageNode(payload);

              if ($isRangeSelection(selection)) {
                selection.insertNodes([imageNode]);
              } else {
                const root = $getRoot();
                const children = root.getChildren();

                if (children.length > 0) {
                  const lastChild = children[children.length - 1];
                  lastChild.insertAfter(imageNode);
                } else {
                  root.append(imageNode);
                }
              }

              if (isCommentEditor) {
                editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
              } else {
                editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
              }

              // Always wrap images in CommentableParagraphNode to make them commentable
              const wrappedImage = $wrapNodeInElement(imageNode, $createCommentableParagraphNode);

              if (!shouldFocus) {
                const paragraphNode = $createParagraphNode();
                wrappedImage.insertAfter(paragraphNode);
                paragraphNode.select();
                editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
              } else {
                const paragraphNodeBefore = $createParagraphNode();
                const paragraphNodeAfter = $createParagraphNode();

                wrappedImage.insertBefore(paragraphNodeBefore);
                wrappedImage.insertAfter(paragraphNodeAfter);

                paragraphNodeAfter.select();
                requestAnimationFrame(() => {
                  editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
                  requestAnimationFrame(() => {
                    const element = editor.getElementByKey(paragraphNodeAfter.getKey());
                    if (element) {
                      setTimeout(() => {
                        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }, 100);
                    }
                  });
                });
              }
            });
          });

          return true;
        },
        COMMAND_PRIORITY_EDITOR
      ),
      editor.registerCommand<InsertVideoPayload[]>(
        INSERT_VIDEO_COMMAND,
        (payloads) => {
          if (!Array.isArray(payloads)) {
            payloads = [payloads];
          }

          editor.update(() => {
            const selection = $getSelection();

            payloads.forEach((payload) => {
              const videoNode = $createVideoNode(payload);

              if ($isRangeSelection(selection)) {
                selection.insertNodes([videoNode]);
              } else {
                const root = $getRoot();
                const children = root.getChildren();
                if (children.length > 0) {
                  const lastChild = children[children.length - 1];
                  lastChild.insertAfter(videoNode);
                } else {
                  root.append(videoNode);
                }
              }

              if (isCommentEditor) {
                editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
              } else {
                editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
              }

              // Always wrap videos in CommentableParagraphNode to make them commentable
              const wrappedVideo = $wrapNodeInElement(videoNode, $createCommentableParagraphNode);

              if (!shouldFocus) {
                const paragraphNode = $createParagraphNode();
                wrappedVideo.insertAfter(paragraphNode);
                paragraphNode.select();
                editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
              } else {
                const paragraphNodeBefore = $createParagraphNode();
                const paragraphNodeAfter = $createParagraphNode();

                wrappedVideo.insertBefore(paragraphNodeBefore);
                wrappedVideo.insertAfter(paragraphNodeAfter);

                paragraphNodeAfter.select();
                requestAnimationFrame(() => {
                  editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
                  requestAnimationFrame(() => {
                    const element = editor.getElementByKey(paragraphNodeAfter.getKey());
                    if (element) {
                      setTimeout(() => {
                        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }, 100);
                    }
                  });
                });
              }
            });
          });

          return true;
        },
        COMMAND_PRIORITY_EDITOR
      ),
      editor.registerCommand<InsertAudioPayload[]>(
        INSERT_AUDIO_COMMAND,
        (payloads) => {
          if (!Array.isArray(payloads)) {
            payloads = [payloads];
          }

          editor.update(() => {
            const selection = $getSelection();

            payloads.forEach((payload) => {
              const audioNode = $createAudioNode(payload);

              if ($isRangeSelection(selection)) {
                selection.insertNodes([audioNode]);
              } else {
                const root = $getRoot();
                const children = root.getChildren();

                if (children.length > 0) {
                  const lastChild = children[children.length - 1];
                  lastChild.insertAfter(audioNode);
                } else {
                  root.append(audioNode);
                }
              }

              if (isCommentEditor) {
                editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
              } else {
                editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
              }

              // Always wrap audio in CommentableParagraphNode to make them commentable
              const wrappedAudio = $wrapNodeInElement(audioNode, $createCommentableParagraphNode);

              if (!shouldFocus) {
                const paragraphNode = $createParagraphNode();
                wrappedAudio.insertAfter(paragraphNode);
                paragraphNode.select();
                editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
              } else {
                const paragraphNodeBefore = $createParagraphNode();
                const paragraphNodeAfter = $createParagraphNode();

                wrappedAudio.insertBefore(paragraphNodeBefore);
                wrappedAudio.insertAfter(paragraphNodeAfter);

                paragraphNodeAfter.select();

                requestAnimationFrame(() => {
                  editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'left');
                  requestAnimationFrame(() => {
                    const element = editor.getElementByKey(paragraphNodeAfter.getKey());
                    if (element) {
                      setTimeout(() => {
                        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }, 100);
                    }
                  });
                });
              }
            });
          });

          return true;
        },
        COMMAND_PRIORITY_EDITOR
      ),
      editor.registerCommand<DragEvent>(
        DRAGSTART_COMMAND,
        (event) => {
          return $onDragStart(event);
        },
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand<DragEvent>(
        DRAGOVER_COMMAND,
        (event) => {
          return $onDragover(event);
        },
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand<DragEvent>(
        DROP_COMMAND,
        (event) => {
          return $onDrop(event, editor);
        },
        COMMAND_PRIORITY_HIGH
      )
    );
  }, [editor]);

  return null;
}

const TRANSPARENT_IMAGE =
  'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
const img = document.createElement('img');
img.src = TRANSPARENT_IMAGE;

function $onDragStart(event: DragEvent): boolean {
  const node = $getMediaNodeInSelection();
  if (!node) {
    return false;
  }
  const dataTransfer = event.dataTransfer;
  if (!dataTransfer) {
    return false;
  }
  const isImage = $isImageNode(node);
  const isVideo = $isVideoNode(node);
  const type = isImage ? 'image' : isVideo ? 'video' : 'audio';

  dataTransfer.setData('text/plain', '_');
  dataTransfer.setDragImage(img, 0, 0);
  dataTransfer.setData(
    'application/x-lexical-drag',
    JSON.stringify({
      data: {
        altText: node.__altText || '',
        caption: node.__caption || '',
        height: node.__height,
        key: node.getKey(),
        maxWidth: node.__maxWidth,
        showCaption: node.__showCaption,
        src: node.__src,
        width: node.__width,
        ...(type === 'video' && {
          autoplay: node.__autoplay,
          loop: node.__loop
        })
      },
      type
    })
  );

  return true;
}

function $onDragover(event: DragEvent): boolean {
  const node = $getMediaNodeInSelection();
  if (!node) {
    return false;
  }
  if (!canDropMedia(event)) {
    event.preventDefault();
  }
  return true;
}

function $onDrop(event: DragEvent, editor: LexicalEditor): boolean {
  const node = $getMediaNodeInSelection();
  if (!node) {
    return false;
  }
  const data = getDragMediaData(event);
  if (!data) {
    return false;
  }
  event.preventDefault();
  if (canDropMedia(event)) {
    const range = getDragSelection(event);
    node.remove();
    const rangeSelection = $createRangeSelection();
    if (range !== null && range !== undefined) {
      rangeSelection.applyDOMRange(range);
    }
    $setSelection(rangeSelection);

    const command =
      data.type === 'image'
        ? INSERT_IMAGE_COMMAND
        : data.type === 'audio'
          ? INSERT_AUDIO_COMMAND
          : INSERT_VIDEO_COMMAND;
    editor.dispatchCommand(command, data);
    editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, 'center');
  }
  return true;
}

function $getMediaNodeInSelection(): any {
  const selection = $getSelection();
  if (!$isNodeSelection(selection)) {
    return null;
  }
  const nodes = selection.getNodes();
  const node = nodes[0];
  return $isImageNode(node) || $isVideoNode(node) || $isAudioNode(node) ? node : null;
}

function getDragMediaData(event: DragEvent): any {
  const dragData = event.dataTransfer?.getData('application/x-lexical-drag');
  if (!dragData) {
    return null;
  }
  const { type, data } = JSON.parse(dragData);
  if (type !== 'image' && type !== 'video' && type !== 'audio') {
    return null;
  }

  return { ...data, type };
}

declare global {
  interface DragEvent {
    rangeOffset?: number;
    rangeParent?: Node;
  }
}

function canDropMedia(event: DragEvent): boolean {
  const target = event.target;
  return !!(
    target &&
    target instanceof HTMLElement &&
    !target.closest('code, span.editor-media') &&
    target.parentElement &&
    target.parentElement.closest('div.ContentEditable__root')
  );
}
function getDragSelection(event: DragEvent): Range | null | undefined {
  let range;
  const target = event.target as null | Element | Document;
  const targetWindow =
    target == null
      ? null
      : target.nodeType === 9
        ? (target as Document).defaultView
        : (target as Element).ownerDocument.defaultView;
  const domSelection = getDOMSelection(targetWindow);
  if (document.caretRangeFromPoint) {
    range = document.caretRangeFromPoint(event.clientX, event.clientY);
  } else if (event.rangeParent && domSelection !== null) {
    domSelection.collapse(event.rangeParent, event.rangeOffset || 0);
    range = domSelection.getRangeAt(0);
  } else {
    throw Error('Cannot get the selection when dragging');
  }

  return range;
}
